/**
 * CreatorPanel.jsx
 * frontend/src/pages/CreatorPanel.jsx
 *
 * Thêm route vào App.js (KHÔNG bọc ProtectedRoute):
 *   import CreatorPanel from "./pages/CreatorPanel";
 *   <Route path="/creator" element={<CreatorPanel />} />
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: BASE, withCredentials: true });

// ─── Design tokens ───────────────────────────────────────────
const C = {
  bg: "#0a0f0d",
  panel: "#111a14",
  card: "#162019",
  border: "#1e3024",
  borderGlow: "#2d5a3a",
  accent: "#22c55e",
  accentDim: "#16a34a",
  accentSoft: "rgba(34,197,94,0.08)",
  accentSoft2: "rgba(34,197,94,0.15)",
  red: "#ef4444",
  redSoft: "rgba(239,68,68,0.1)",
  amber: "#f59e0b",
  amberSoft: "rgba(245,158,11,0.1)",
  blue: "#60a5fa",
  blueSoft: "rgba(96,165,250,0.1)",
  purple: "#c084fc",
  purpleSoft: "rgba(192,132,252,0.1)",
  text: "#e2ffe8",
  textMuted: "#6b9a74",
  textDim: "#3d6644",
};

// ─── Navigation keys ─────────────────────────────────────────
const NAV_KEYS = [
  { key: "dashboard",    label: "Dashboard" },
  { key: "orders",       label: "Đơn hàng" },
  { key: "products",     label: "Sản phẩm" },
  { key: "materials",    label: "Nguyên liệu" },
  { key: "production",   label: "Sản xuất" },
  { key: "customers",    label: "Khách hàng" },
  { key: "customerCare", label: "Chăm sóc KH" },
  { key: "insights",     label: "Insights AI" },
  { key: "suppliers",    label: "Nhà cung cấp" },
  { key: "debts",        label: "Công nợ" },
  { key: "delivery",     label: "Giao hàng" },
  { key: "users",        label: "Người dùng" },
  { key: "reports",      label: "Báo cáo" },
  { key: "finance",      label: "Tài chính" },
  { key: "settings",     label: "Cài đặt" },
];

// ─── Feature keys — grouped & detailed ───────────────────────
const FEATURE_GROUPS = [
  {
    group: "🛒 Đơn hàng",
    color: C.amber,
    colorSoft: C.amberSoft,
    items: [
      { key: "orders.print",        label: "In / Xuất PDF danh sách đơn",    desc: "Nút In/PDF trên trang danh sách đơn hàng" },
      { key: "orders.duplicate",    label: "Nhân bản đơn hàng",              desc: "Icon nhân bản từng đơn (tạo đơn copy)" },
      { key: "orders.edit",         label: "Chỉnh sửa đơn hàng",            desc: "Nút chỉnh sửa đơn — cân bằng lại kho & tiền, yêu cầu PIN2" },
      { key: "orders.export",       label: "Xuất Excel danh sách đơn",       desc: "Nút xuất file Excel (.xlsx) đơn hàng" },
      { key: "orders.delete",       label: "Xóa đơn hàng",                   desc: "Nút xóa đơn — yêu cầu PIN2" },
      { key: "orders.bill_print",   label: "In hóa đơn / bill cho KH",       desc: "In bill từng đơn trong trang chi tiết đơn" },
    ],
  },
  {
    group: "📦 Sản phẩm",
    color: C.blue,
    colorSoft: C.blueSoft,
    items: [
      { key: "products.recipe",     label: "Xem & sửa công thức NVL",        desc: "Icon BookOpen — xem/thiết lập nguyên liệu cho từng sản phẩm" },
      { key: "products.import",     label: "Import sản phẩm (Excel/CSV)",    desc: "Nút upload file import hàng loạt sản phẩm" },
      { key: "products.export",     label: "Xuất Excel sản phẩm",            desc: "Nút xuất danh sách sản phẩm ra file Excel" },
      { key: "products.delete",     label: "Xóa sản phẩm",                   desc: "Nút xóa từng sản phẩm" },
      { key: "products.image",      label: "Upload ảnh sản phẩm",            desc: "Ô tải ảnh đại diện khi tạo/sửa sản phẩm" },
      { key: "products.print",      label: "In danh sách sản phẩm",          desc: "Nút in/PDF danh sách sản phẩm" },
      { key: "products.sort",       label: "Sắp xếp thứ tự sản phẩm (kéo)", desc: "Nút 'Sắp xếp thứ tự' — cho phép kéo thả để đổi thứ tự hiển thị sản phẩm" },
    ],
  },
  {
    group: "🌾 Nguyên liệu & Kho",
    color: "#4ade80",
    colorSoft: C.accentSoft2,
    items: [
      { key: "materials.stockin",   label: "Nhập kho nguyên liệu",           desc: "Nút nhập kho (stock in)" },
      { key: "materials.adjust",    label: "Điều chỉnh tồn kho",             desc: "Nút điều chỉnh số lượng tồn" },
      { key: "materials.delete",    label: "Xóa nguyên liệu",                desc: "Nút xóa từng loại nguyên liệu" },
      { key: "materials.export",    label: "Xuất báo cáo XNT (Excel/In)",    desc: "Nút tải CSV / in bảng xuất-nhập-tồn" },
      { key: "materials.expiring",  label: "Cảnh báo hạn sử dụng",          desc: "Hiện cảnh báo NVL sắp hết hạn" },
    ],
  },
  {
    group: "🏭 Sản xuất",
    color: C.purple,
    colorSoft: C.purpleSoft,
    items: [
      { key: "production.forecast", label: "Tab Dự báo sản xuất AI",         desc: "Tab Dự báo — đề xuất sản xuất bao nhiêu mỗi ngày" },
      { key: "production.plan",     label: "Kế hoạch sản xuất & mua NVL",   desc: "Lập và in kế hoạch sản xuất / mua nguyên liệu" },
      { key: "production.export",   label: "Xuất CSV XNT sản xuất",          desc: "Nút tải CSV bảng xuất-nhập-tồn sản xuất" },
      { key: "production.print",    label: "In bảng xuất-nhập-tồn",          desc: "Nút in XNT nguyên liệu và lịch sử biến động" },
      { key: "production.delete",   label: "Xóa lô sản xuất",               desc: "Nút xóa từng lô đã ghi" },
    ],
  },
  {
    group: "👥 Khách hàng",
    color: "#2dd4bf",
    colorSoft: "rgba(45,212,191,0.1)",
    items: [
      { key: "customers.export",    label: "Xuất Excel khách hàng",          desc: "Nút tải danh sách KH ra file Excel" },
      { key: "customers.import",    label: "Import KH (Excel)",              desc: "Nút upload file import hàng loạt KH" },
      { key: "customers.delete",    label: "Xóa khách hàng",                 desc: "Nút xóa từng khách hàng" },
      { key: "customers.print",     label: "In danh sách khách hàng",        desc: "Nút in/PDF danh sách KH" },
      { key: "customers.care_note", label: "Ghi chú chăm sóc KH",           desc: "Ô nhập ghi chú tương tác / lịch sử chăm sóc" },
    ],
  },
  {
    group: "💰 Công nợ",
    color: C.red,
    colorSoft: C.redSoft,
    items: [
      { key: "debts.collect",       label: "Thu tiền nợ khách hàng",         desc: "Nút 'Thu tiền' trên bảng công nợ KH" },
      { key: "debts.pay_supplier",  label: "Thanh toán nợ NCC",              desc: "Nút thanh toán tiền nợ nhà cung cấp" },
      { key: "debts.delete",        label: "Xóa giao dịch công nợ",          desc: "Xóa lịch sử thu/trả nợ" },
      { key: "debts.history",       label: "Xem lịch sử thanh toán",         desc: "Tab lịch sử các giao dịch thu/trả nợ" },
    ],
  },
  {
    group: "🚴 Giao hàng",
    color: C.amber,
    colorSoft: C.amberSoft,
    items: [
      { key: "delivery.assign",     label: "Phân công shipper",              desc: "Gán shipper cho từng đơn hàng" },
      { key: "delivery.map",        label: "Xem bản đồ giao hàng",          desc: "Nút/tab xem địa chỉ giao trên bản đồ" },
      { key: "delivery.export",     label: "Xuất danh sách giao hàng",       desc: "Xuất file Excel danh sách cần giao" },
    ],
  },
  {
    group: "📊 Báo cáo & Tài chính",
    color: C.blue,
    colorSoft: C.blueSoft,
    items: [
      { key: "reports.print",       label: "In / PDF báo cáo",              desc: "Nút in/PDF mọi loại báo cáo" },
      { key: "reports.export",      label: "Xuất Excel báo cáo",             desc: "Nút tải báo cáo ra file Excel" },
      { key: "finance.delete",      label: "Xóa giao dịch tài chính",        desc: "Nút xóa từng khoản thu/chi" },
      { key: "finance.category",    label: "Quản lý danh mục tài chính",     desc: "Tạo/sửa/xóa danh mục thu chi" },
    ],
  },
  {
    group: "🤖 AI & Chat",
    color: C.purple,
    colorSoft: C.purpleSoft,
    items: [
      { key: "ai.chat_widget",      label: "Widget Chat AI (góc màn hình)", desc: "Nút chat AI nổi ở góc dưới phải mọi trang" },
      { key: "ai.insights_combos",  label: "Insights: Combo bán chạy",      desc: "Tab phân tích combo sản phẩm mua kèm" },
      { key: "ai.insights_scoring", label: "Insights: Chấm điểm KH",       desc: "Tab xếp hạng khách hàng theo điểm trung thành" },
      { key: "ai.insights_suggest", label: "Insights: Gợi ý AI tự động",   desc: "Nút gọi AI phân tích và đưa ra khuyến nghị" },
      { key: "ai.production_forecast", label: "Dự báo SX bằng AI",         desc: "Tính năng dự báo số lượng cần sản xuất mỗi ngày" },
    ],
  },
  {
    group: "⚙️ Hệ thống",
    color: C.red,
    colorSoft: C.redSoft,
    items: [
      { key: "system.reset",        label: "Reset / Xóa toàn bộ dữ liệu demo", desc: "Nút xóa toàn bộ — chỉ dùng trong môi trường demo" },
      { key: "system.backup",       label: "Backup & Export toàn bộ (ZIP)",     desc: "Nút tải về file ZIP backup toàn bộ DB" },
      { key: "system.import_excel", label: "Import hàng loạt từ Excel",         desc: "Tính năng import toàn bộ dữ liệu từ Excel" },
      { key: "system.pin2_manage",  label: "Quản lý PIN xác nhận (PIN2)",       desc: "Cài đặt / đổi PIN2 bảo vệ thao tác nguy hiểm" },
    ],
  },
];

// Flatten all feature keys for backward-compat
const ALL_FEATURE_KEYS = FEATURE_GROUPS.flatMap(g => g.items);

const ROLES = ["admin", "manager", "coordinator", "warehouse", "accountant", "shipper", "staff"];
const ROLE_LABELS = {
  admin: "Admin", manager: "Quản lý", coordinator: "Điều phối",
  warehouse: "Kho", accountant: "Kế toán", shipper: "Giao hàng", staff: "Nhân viên",
};

// ─── Styles ──────────────────────────────────────────────────
const s = {
  page: {
    minHeight: "100vh", background: C.bg, color: C.text,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
    display: "flex", flexDirection: "column",
  },
  loginWrap: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: `radial-gradient(ellipse at 30% 40%, rgba(34,197,94,0.06) 0%, transparent 60%), ${C.bg}`,
  },
  loginBox: {
    width: 360, padding: "40px 36px", borderRadius: 16,
    background: C.panel, border: `1px solid ${C.border}`,
    boxShadow: `0 0 60px rgba(34,197,94,0.06), 0 24px 64px rgba(0,0,0,0.6)`,
  },
  header: {
    padding: "16px 28px", borderBottom: `1px solid ${C.border}`,
    background: C.panel, display: "flex", alignItems: "center", justifyContent: "space-between",
    position: "sticky", top: 0, zIndex: 100,
  },
  main: { flex: 1, padding: "28px", maxWidth: 1200, margin: "0 auto", width: "100%" },
  tabs: { display: "flex", gap: 4, marginBottom: 28, borderBottom: `1px solid ${C.border}` },
  tab: (active) => ({
    padding: "10px 20px", borderRadius: "8px 8px 0 0", cursor: "pointer", fontSize: 13,
    fontWeight: 600, border: "none", outline: "none",
    background: active ? C.card : "transparent",
    color: active ? C.accent : C.textMuted,
    borderBottom: active ? `2px solid ${C.accent}` : "2px solid transparent",
    transition: "all 0.15s",
  }),
  card: {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: "20px 24px", marginBottom: 20,
  },
  sectionTitle: { fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: C.textDim, textTransform: "uppercase", marginBottom: 16 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  toggleRow: (on) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 12px", borderRadius: 8, marginBottom: 4, cursor: "pointer",
    background: on ? C.accentSoft : "transparent",
    border: `1px solid ${on ? C.borderGlow : "transparent"}`,
    transition: "all 0.15s",
  }),
  featureRow: (on, color, colorSoft) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 14px", borderRadius: 8, marginBottom: 6, cursor: "pointer",
    background: on ? (colorSoft || C.accentSoft) : "rgba(0,0,0,0.2)",
    border: `1px solid ${on ? (color + "44" || C.borderGlow) : C.border + "44"}`,
    transition: "all 0.15s",
  }),
  toggle: (on) => ({
    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
    background: on ? C.accent : "#2a3a2e",
    position: "relative", transition: "background 0.2s", flexShrink: 0,
    border: "none", outline: "none",
  }),
  toggleDot: (on) => ({
    position: "absolute", top: 3, left: on ? 18 : 3,
    width: 14, height: 14, borderRadius: "50%",
    background: on ? "#fff" : C.textDim, transition: "left 0.2s",
  }),
  input: {
    width: "100%", background: "#0d160f", border: `1px solid ${C.border}`,
    borderRadius: 8, color: C.text, padding: "9px 14px", fontSize: 13,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  },
  label: { fontSize: 11, color: C.textMuted, marginBottom: 6, display: "block", fontWeight: 600 },
  btn: (variant = "primary") => ({
    padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700,
    cursor: "pointer", border: "none", outline: "none", transition: "all 0.15s",
    ...(variant === "primary" ? {
      background: C.accent, color: "#071009",
    } : variant === "danger" ? {
      background: C.redSoft, color: C.red, border: `1px solid ${C.red}33`,
    } : variant === "ghost" ? {
      background: C.accentSoft, color: C.accent, border: `1px solid ${C.borderGlow}`,
    } : {
      background: "#1a2e1e", color: C.textMuted, border: `1px solid ${C.border}`,
    }),
  }),
  badge: (role) => {
    const map = {
      admin: { bg: "rgba(239,68,68,0.15)", color: "#f87171" },
      manager: { bg: "rgba(245,158,11,0.15)", color: "#fbbf24" },
      coordinator: { bg: "rgba(59,130,246,0.15)", color: "#60a5fa" },
      warehouse: { bg: "rgba(168,85,247,0.15)", color: "#c084fc" },
      accountant: { bg: "rgba(20,184,166,0.15)", color: "#2dd4bf" },
      shipper: { bg: "rgba(34,197,94,0.15)", color: "#4ade80" },
      staff: { bg: "rgba(107,114,128,0.2)", color: "#9ca3af" },
    };
    const m = map[role] || map.staff;
    return { padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color };
  },
  statCard: {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: "16px 20px", textAlign: "center",
  },
  groupHeader: (color) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 12, paddingBottom: 10,
    borderBottom: `1px solid ${color}33`,
  }),
  groupTitle: (color) => ({
    fontSize: 13, fontWeight: 700, color: color,
    letterSpacing: "0.04em",
  }),
  groupCount: (hiddenCount, total) => ({
    fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20,
    background: hiddenCount > 0 ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)",
    color: hiddenCount > 0 ? C.red : C.accent,
  }),
  quickActionsBar: {
    display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center",
  },
};

// ─── Toggle Component ────────────────────────────────────────
function Toggle({ on, onChange }) {
  return (
    <button style={s.toggle(on)} onClick={(e) => { e.stopPropagation(); onChange(); }}>
      <div style={s.toggleDot(on)} />
    </button>
  );
}

// ─── Input Component ─────────────────────────────────────────
function Input({ label, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={s.label}>{label}</label>}
      <input style={s.input} {...props} />
    </div>
  );
}

// ─── LOGIN SCREEN ────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true); setErr("");
    try {
      await api.post("/creator/login", { password: pw });
      onLogin();
    } catch (e) {
      setErr(e.response?.data?.detail || "Sai mật khẩu");
    } finally { setLoading(false); }
  };

  return (
    <div style={s.loginWrap}>
      <div style={s.loginBox}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `linear-gradient(135deg, ${C.accent}, ${C.accentDim})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px", fontSize: 24,
            boxShadow: `0 8px 32px rgba(34,197,94,0.3)`,
          }}>⚡</div>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "0.05em", color: C.text }}>
            CREATOR PANEL
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4, letterSpacing: "0.1em" }}>
            RESTRICTED ACCESS
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={s.label}>Mật khẩu Creator</label>
          <input
            style={s.input}
            type="password"
            placeholder="••••••••••••"
            value={pw}
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === "Enter" && submit()}
            autoFocus
          />
        </div>
        {err && <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>✗ {err}</div>}
        <button
          style={{ ...s.btn("primary"), width: "100%", padding: "11px 0", fontSize: 14 }}
          onClick={submit} disabled={loading}
        >
          {loading ? "Đang xác thực..." : "Đăng nhập →"}
        </button>

        <div style={{ marginTop: 20, fontSize: 10, color: C.textDim, textAlign: "center", letterSpacing: "0.08em" }}>
          Trang này không được index bởi bất kỳ bot nào
        </div>
      </div>
    </div>
  );
}

// ─── TAB: THỐNG KÊ ──────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// TAB LỊCH SỬ THAO TÁC + TRUY CẬP
// ─────────────────────────────────────────────────────────────────────────────
function TabHistory() {
  const [auditLogs, setAuditLogs]     = React.useState([]);
  const [accessLogs, setAccessLogs]   = React.useState([]);
  const [loading, setLoading]         = React.useState(true);
  const [subTab, setSubTab]           = React.useState("audit");   // "audit" | "access"
  const [search, setSearch]           = React.useState("");
  const [eventFilter, setEventFilter] = React.useState("");
  const [unlockingKey, setUnlockingKey] = React.useState(null);
  const [kickingId, setKickingId]     = React.useState(null);  // user_id đang được kick

  const kickUser = async (log) => {
    if (!log.user_id) return;
    if (!window.confirm(`Kick đăng xuất "${log.name || log.email}"?\nHọ sẽ bị đăng xuất ngay và có thể đăng nhập lại bình thường.`)) return;
    setKickingId(log.user_id);
    try {
      await api.post(`/creator/kick-user/${log.user_id}`);
      alert(`✅ Đã kick "${log.name || log.email}" thành công.`);
    } catch {
      alert("❌ Kick thất bại, thử lại.");
    } finally {
      setKickingId(null);
    }
  };

  const unlockIp = async (log) => {
    const key = `${log.ip}:${log.email}`;
    if (!window.confirm(`Mở khoá ngay cho "${log.email}" (IP: ${log.ip})?\nHọ có thể đăng nhập lại ngay mà không cần đợi 15 phút.`)) return;
    setUnlockingKey(key);
    try {
      await api.post("/creator/unlock-ip", { email: log.email, ip: log.ip });
      alert(`✅ Đã mở khoá cho "${log.email}" thành công.`);
      // Reload access log
      const res = await api.get("/creator/access-log", { params: { limit: 300 } });
      setAccessLogs(res.data || []);
    } catch (e) {
      alert("❌ Mở khoá thất bại: " + (e?.response?.data?.detail || "thử lại."));
    } finally {
      setUnlockingKey(null);
    }
  };

  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/creator/audit-log", { params: { limit: 300 } }),
      api.get("/creator/access-log", { params: { limit: 300 } }),
    ]).then(([a, b]) => {
      setAuditLogs(a.data || []);
      setAccessLogs(b.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const EVENT_LABELS = {
    // Creator
    login_success:       { label: "Đăng nhập Creator",     icon: "🔑", color: "#16A085" },
    login_failed:        { label: "Đăng nhập thất bại",    icon: "🚫", color: "#C0392B" },
    config_updated:      { label: "Thay đổi cấu hình",     icon: "⚙️", color: "#2980B9" },
    user_created:        { label: "Thêm người dùng",        icon: "➕", color: "#27AE60" },
    user_updated:        { label: "Cập nhật người dùng",   icon: "✏️", color: "#E67E22" },
    user_deleted:        { label: "Xóa người dùng",         icon: "🗑️", color: "#C0392B" },
    user_toggled:        { label: "Bật/Tắt người dùng",    icon: "🔄", color: "#8E44AD" },
    user_kicked:         { label: "Kick đăng xuất",         icon: "👢", color: "#C0392B" },
    // Đơn hàng
    created_order:       { label: "Tạo đơn hàng",           icon: "🛒", color: "#27AE60" },
    updated_order:       { label: "Cập nhật đơn hàng",      icon: "✏️", color: "#E67E22" },
    deleted_order:       { label: "Xóa đơn hàng",           icon: "🗑️", color: "#C0392B" },
    // Sản phẩm
    created_product:     { label: "Thêm sản phẩm",          icon: "📦", color: "#27AE60" },
    updated_product:     { label: "Cập nhật sản phẩm",      icon: "✏️", color: "#E67E22" },
    deleted_product:     { label: "Xóa sản phẩm",           icon: "🗑️", color: "#C0392B" },
    // Nguyên vật liệu
    created_material:    { label: "Thêm nguyên liệu",        icon: "🧱", color: "#27AE60" },
    updated_material:    { label: "Cập nhật nguyên liệu",   icon: "✏️", color: "#E67E22" },
    deleted_material:    { label: "Xóa nguyên liệu",         icon: "🗑️", color: "#C0392B" },
    // Khách hàng
    created_customer:    { label: "Thêm khách hàng",         icon: "👤", color: "#27AE60" },
    deleted_customer:    { label: "Xóa khách hàng",          icon: "🗑️", color: "#C0392B" },
    // Kho
    created_stock_in:    { label: "Nhập kho",                icon: "📥", color: "#16A085" },
    // Sản xuất
    created_production:  { label: "Tạo lô sản xuất",         icon: "🏭", color: "#8E44AD" },
    // Thu chi
    created_finance_transaction: { label: "Thêm thu/chi",   icon: "💰", color: "#E67E22" },
  };

  // Map event key từ backend: "created" + entity → event key
  const mapEvent = (log) => {
    if (log.entity) return `${log.event}_${log.entity}`;
    return log.event;
  };

  const stripAccents = (s) => {
    if (!s) return "";
    return String(s).toLowerCase().replace(/đ/g, "d").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  // Filter audit logs
  const filteredAudit = auditLogs.filter(log => {
    const matchEvent = !eventFilter || log.event === eventFilter;
    const matchSearch = !search || [log.target, log.email, log.event, log.ip]
      .some(f => stripAccents(f ?? "").includes(stripAccents(search)));
    return matchEvent && matchSearch;
  });

  // Filter access logs
  const filteredAccess = accessLogs.filter(log => {
    return !search || [log.name, log.email, log.role, log.ip]
      .some(f => stripAccents(f ?? "").includes(stripAccents(search)));
  });

  const tdStyle = { padding: "10px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 12.5, verticalAlign: "top" };
  const thStyle = { padding: "8px 12px", background: C.surface, fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.08em", color: C.textDim, borderBottom: `1px solid ${C.border}` };

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[
          { key: "audit",  label: "🕵️ Lịch sử thao tác Creator" },
          { key: "access", label: "🔐 Lịch sử truy cập hệ thống" },
        ].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            padding: "7px 18px", borderRadius: 8, border: `1px solid ${subTab === t.key ? C.accent : C.border}`,
            background: subTab === t.key ? C.accent : C.surface,
            color: subTab === t.key ? "#fff" : C.text, cursor: "pointer", fontSize: 13, fontWeight: 600,
          }}>
            {t.label}
            <span style={{ marginLeft: 6, background: "rgba(255,255,255,0.25)", borderRadius: 99,
              padding: "1px 7px", fontSize: 11 }}>
              {t.key === "audit" ? filteredAudit.length : filteredAccess.length}
            </span>
          </button>
        ))}
      </div>

      {/* Thanh tìm kiếm */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
            color: C.textDim, pointerEvents: "none" }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={subTab === "audit" ? "Tìm theo tên, email, sự kiện..." : "Tìm theo tên, email, IP..."}
            style={{ width: "100%", padding: "8px 10px 8px 32px", borderRadius: 8,
              border: `1px solid ${C.border}`, fontSize: 13, background: C.surface, color: C.text, boxSizing: "border-box" }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%",
              transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer",
              color: C.textDim, fontSize: 16 }}>×</button>
          )}
        </div>
        {subTab === "audit" && (
          <select value={eventFilter} onChange={e => setEventFilter(e.target.value)} style={{
            padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
            fontSize: 13, background: C.surface, color: C.text, cursor: "pointer",
          }}>
            <option value="">Tất cả sự kiện</option>
            {Object.entries(EVENT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.icon} {v.label}</option>
            ))}
          </select>
        )}
        <button onClick={() => {
          setLoading(true);
          Promise.all([
            api.get("/creator/audit-log", { params: { limit: 300 } }),
            api.get("/creator/access-log", { params: { limit: 300 } }),
          ]).then(([a, b]) => { setAuditLogs(a.data || []); setAccessLogs(b.data || []); })
          .finally(() => setLoading(false));
        }} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
          background: C.surface, color: C.text, cursor: "pointer", fontSize: 13 }}>
          🔄 Làm mới
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: C.textDim }}>Đang tải...</div>
      ) : subTab === "audit" ? (
        /* ── Bảng thao tác Creator ── */
        <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Thời gian</th>
                <th style={thStyle}>Sự kiện</th>
                <th style={thStyle}>Đối tượng</th>
                <th style={thStyle}>Chi tiết</th>
                <th style={thStyle}>IP</th>
              </tr>
            </thead>
            <tbody>
              {filteredAudit.length === 0 ? (
                <tr><td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: C.textDim, padding: 32 }}>
                  {search || eventFilter ? "Không tìm thấy kết quả" : "Chưa có lịch sử thao tác"}
                </td></tr>
              ) : filteredAudit.map((log, i) => {
                const evtKey = log.entity ? `${log.event}_${log.entity}` : log.event;
                const cfg = EVENT_LABELS[evtKey] || { label: `${log.event}${log.entity ? " · "+log.entity : ""}`, icon: "📋", color: C.textDim };
                const detail = (() => {
                  const d = log.detail || {};
                  const parts = [];
                  // Hiện thông tin tùy entity
                  if (d.order_code) parts.push(`Đơn: ${d.order_code}`);
                  if (d.total !== undefined) parts.push(`Tổng: ${Number(d.total).toLocaleString("vi-VN")}đ`);
                  if (d.payment) parts.push(`HTTT: ${d.payment}`);
                  if (d.items?.length) parts.push(`SP: ${d.items.join(", ")}`);
                  if (d.sku) parts.push(`SKU: ${d.sku}`);
                  if (d.price !== undefined) parts.push(`Giá: ${Number(d.price).toLocaleString("vi-VN")}đ`);
                  if (d.unit) parts.push(`ĐVT: ${d.unit}`);
                  if (d.stock !== undefined) parts.push(`Tồn: ${d.stock}`);
                  if (d.qty !== undefined) parts.push(`SL: ${d.qty}`);
                  if (d.kind) parts.push(`Loại: ${d.kind}`);
                  if (d.note) parts.push(`Ghi chú: ${d.note}`);
                  if (d.phone) parts.push(`SĐT: ${d.phone}`);
                  if (d.amount !== undefined) parts.push(`Số tiền: ${Number(d.amount).toLocaleString("vi-VN")}đ`);
                  if (d.type) parts.push(`Loại: ${d.type}`);
                  if (d.description) parts.push(d.description);
                  // Changes
                  if (d.changes && typeof d.changes === "object") {
                    const chParts = Object.entries(d.changes).map(([k, v]) => {
                      if (v && typeof v === "object" && "từ" in v)
                        return `${k}: ${JSON.stringify(v["từ"])} → ${JSON.stringify(v["thành"])}`;
                      return `${k}: ${JSON.stringify(v)}`;
                    });
                    parts.push(...chParts);
                  }
                  if (log.is_active !== undefined) parts.push(log.is_active ? "✅ Kích hoạt" : "🔴 Vô hiệu hóa");
                  // Fallback cũ
                  if (!parts.length && log.changes?.length) {
                    return log.changes.map(ch =>
                      typeof ch === "object" && ch.field
                        ? `${ch.field}: ${JSON.stringify(ch.old)} → ${JSON.stringify(ch.new)}`
                        : JSON.stringify(ch)
                    ).join("; ");
                  }
                  return parts.length ? parts.join(" · ") : "—";
                })();
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : `${C.surface}88` }}>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap", color: C.textDim }}>{log.at}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{log.by || "Creator"}</div>
                      {log.role && <div style={{ fontSize: 11, color: C.textDim }}>{log.role}</div>}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600,
                        background: `${cfg.color}18`, color: cfg.color }}>
                        {cfg.icon} {cfg.label}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{log.target || log.email || "—"}</div>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 320, wordBreak: "break-word", color: C.textDim, fontSize: 11.5 }}>
                      {detail}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* ── Bảng truy cập hệ thống ── */
        <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Thời gian</th>
                <th style={thStyle}>Người dùng</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Vai trò</th>
                <th style={thStyle}>Sự kiện</th>
                <th style={thStyle}>IP</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccess.length === 0 ? (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: "center", color: C.textDim, padding: 32 }}>
                  {search ? "Không tìm thấy kết quả" : "Chưa có lịch sử truy cập"}
                </td></tr>
              ) : filteredAccess.map((log, i) => {
                const evtCfg = {
                  login:        { icon: "🟢", label: "Đăng nhập",  color: "#27AE60" },
                  logout:       { icon: "🔵", label: "Đăng xuất",  color: "#2980B9" },
                  login_failed: { icon: "🟡", label: "Sai mật khẩu", color: "#E67E22" },
                  locked:       { icon: "🔴", label: "Bị khoá 15p", color: "#C0392B" },
                  unlocked:     { icon: "🔓", label: "Đã mở khoá", color: "#8E44AD" },
                }[log.event] || { icon: "⚪", label: log.event || "—", color: C.textDim };
                const unlockKey = `${log.ip}:${log.email}`;
                const isLocked = log.event === "locked";
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : `${C.surface}88` }}>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap", color: C.textDim }}>{log.at}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{log.name || "—"}</td>
                    <td style={{ ...tdStyle, color: C.textDim }}>{log.email || "—"}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: "2px 8px", borderRadius: 99, fontSize: 11,
                        background: log.role === "admin" ? "#27AE6018" : "#2980B918",
                        color: log.role === "admin" ? "#27AE60" : "#2980B9", fontWeight: 600 }}>
                        {log.role || "—"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "2px 9px", borderRadius: 99, fontSize: 11.5, fontWeight: 600,
                        background: `${evtCfg.color}18`, color: evtCfg.color }}>
                        {evtCfg.icon} {evtCfg.label}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: C.textDim, fontFamily: "monospace", fontSize: 11 }}>{log.ip || "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        {isLocked && log.email && log.ip && (
                          <button
                            onClick={() => unlockIp(log)}
                            disabled={unlockingKey === unlockKey}
                            title={`Mở khoá ngay cho ${log.email}`}
                            style={{
                              padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                              border: `1px solid #8E44AD`, cursor: "pointer",
                              background: unlockingKey === unlockKey ? C.surface : "#8E44AD18",
                              color: unlockingKey === unlockKey ? C.textDim : "#8E44AD",
                              opacity: unlockingKey === unlockKey ? 0.6 : 1,
                              transition: "all 0.15s",
                            }}
                          >
                            {unlockingKey === unlockKey ? "..." : "🔓 Mở khoá"}
                          </button>
                        )}
                        {log.user_id ? (
                          <button
                            onClick={() => kickUser(log)}
                            disabled={kickingId === log.user_id}
                            title={`Kick đăng xuất ${log.name || log.email}`}
                            style={{
                              padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                              border: `1px solid ${C.border}`, cursor: "pointer",
                              background: kickingId === log.user_id ? C.surface : "transparent",
                              color: kickingId === log.user_id ? C.textDim : "#C0392B",
                              opacity: kickingId === log.user_id ? 0.6 : 1,
                              transition: "all 0.15s",
                            }}
                          >
                            {kickingId === log.user_id ? "..." : "👢 Kick"}
                          </button>
                        ) : "—"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/creator/stats").then(r => setStats(r.data)).catch(() => {});
  }, []);

  const items = stats ? [
    { label: "Tổng Users", value: stats.total_users, icon: "👤" },
    { label: "Users Hoạt động", value: stats.active_users, icon: "✅" },
    { label: "Tổng Đơn hàng", value: stats.total_orders, icon: "📦" },
    { label: "Khách hàng", value: stats.total_customers, icon: "🧑‍🤝‍🧑" },
    { label: "Sản phẩm", value: stats.total_products, icon: "🛒" },
  ] : [];

  return (
    <div>
      <div style={s.card}>
        <div style={s.sectionTitle}>Tổng quan hệ thống</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
          {items.length === 0
            ? Array(5).fill(0).map((_, i) => (
                <div key={i} style={{ ...s.statCard, opacity: 0.4 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>—</div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>Đang tải...</div>
                </div>
              ))
            : items.map(it => (
                <div key={it.label} style={s.statCard}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{it.icon}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: C.accent }}>{it.value}</div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{it.label}</div>
                </div>
              ))}
        </div>
      </div>

      <div style={s.card}>
        <div style={s.sectionTitle}>Thông tin Creator</div>
        <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 2 }}>
          <div>🔒 Trang này <span style={{ color: C.accent }}>chỉ bạn biết</span> — URL không được index, không hiện trên navigation</div>
          <div>💾 Mọi cấu hình lưu vào <span style={{ color: C.accent }}>MongoDB</span> — đồng bộ mọi thiết bị, mọi browser</div>
          <div>⚡ Thay đổi cấu hình có hiệu lực <span style={{ color: C.accent }}>ngay lập tức</span> với người dùng admin</div>
          <div>🛡️ Khách hàng <span style={{ color: C.accent }}>không thể thêm user</span> — phải thông qua bạn tại trang này</div>
        </div>
      </div>
    </div>
  );
}

// ─── TAB: ẨN/HIỆN UI & TÍNH NĂNG ────────────────────────────
function TabFeatures({ config, setConfig, onSave, saving }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [modalGroup, setModalGroup] = useState(null); // group đang mở modal

  const toggleNav = (key) => {
    const cur = config.hidden_nav || [];
    const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
    setConfig(c => ({ ...c, hidden_nav: next }));
  };

  const toggleFeature = (key) => {
    const cur = config.hidden_features || [];
    const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
    setConfig(c => ({ ...c, hidden_features: next }));
  };

  const toggleGroup = (group, allKeys, makeHidden) => {
    const cur = config.hidden_features || [];
    let next;
    if (makeHidden) {
      // ẩn tất cả trong group
      next = [...new Set([...cur, ...allKeys])];
    } else {
      // hiện tất cả trong group
      next = cur.filter(k => !allKeys.includes(k));
    }
    setConfig(c => ({ ...c, hidden_features: next }));
  };

  const showAllFeatures = () => setConfig(c => ({ ...c, hidden_features: [] }));
  const hideAllFeatures = () => {
    const allKeys = ALL_FEATURE_KEYS.map(f => f.key);
    setConfig(c => ({ ...c, hidden_features: allKeys }));
  };

  const navHidden = config.hidden_nav || [];
  const featHidden = config.hidden_features || [];
  const totalFeatures = ALL_FEATURE_KEYS.length;
  const totalHidden = featHidden.length;

  const filteredGroups = FEATURE_GROUPS.map(group => ({
    ...group,
    items: searchTerm
      ? group.items.filter(item =>
          item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.key.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : group.items,
  })).filter(group => group.items.length > 0);

  return (
    <div>
      {/* ── Sidebar Nav ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={s.sectionTitle}>📌 Ẩn/Hiện Trang trong Sidebar</div>
          <div style={{
            fontSize: 11, fontWeight: 600, padding: "3px 12px", borderRadius: 20,
            background: navHidden.length > 0 ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)",
            color: navHidden.length > 0 ? C.red : C.accent,
          }}>
            {navHidden.length === 0 ? "Tất cả đang hiện" : `${navHidden.length} trang ẩn`}
          </div>
        </div>
        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 14 }}>
          Toggle <span style={{ color: C.accent }}>BẬT</span> = trang HIỆN &nbsp;|&nbsp;
          Toggle <span style={{ color: C.red }}>TẮT</span> = trang BỊ ẨN với Admin
        </div>
        <div style={s.grid2}>
          {NAV_KEYS.map(({ key, label }) => {
            const visible = !navHidden.includes(key);
            return (
              <div key={key} style={s.toggleRow(visible)} onClick={() => toggleNav(key)}>
                <span style={{ fontSize: 13, color: visible ? C.text : C.textDim }}>{label}</span>
                <Toggle on={visible} onChange={() => toggleNav(key)} />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Feature Toggles ── */}
      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={s.sectionTitle}>🔧 Tắt/Bật Tính Năng Chi Tiết</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, padding: "3px 12px", borderRadius: 20,
              background: totalHidden > 0 ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)",
              color: totalHidden > 0 ? C.red : C.accent,
            }}>
              {totalHidden === 0
                ? `Tất cả ${totalFeatures} tính năng đang bật`
                : `${totalHidden}/${totalFeatures} tính năng đang tắt`}
            </div>
          </div>
        </div>

        {/* Quick action bar */}
        <div style={s.quickActionsBar}>
          <input
            style={{ ...s.input, width: 240, padding: "7px 12px" }}
            placeholder="🔍  Tìm tính năng..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button
            style={{ ...s.btn("ghost"), padding: "7px 14px", fontSize: 12 }}
            onClick={showAllFeatures}
            title="Bật tất cả tính năng"
          >
            ✅ Bật tất cả
          </button>
          <button
            style={{ ...s.btn("danger"), padding: "7px 14px", fontSize: 12 }}
            onClick={hideAllFeatures}
            title="Tắt tất cả tính năng"
          >
            🚫 Tắt tất cả
          </button>
          <span style={{ fontSize: 11, color: C.textDim, marginLeft: 4 }}>
            Toggle BẬT = tính năng HIỆN · Toggle TẮT = tính năng BỊ ẨN
          </span>
        </div>

        {/* Groups — bấm để mở modal */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {filteredGroups.map(({ group, color, colorSoft, items }) => {
            const allKeys = items.map(i => i.key);
            const hiddenInGroup = allKeys.filter(k => featHidden.includes(k)).length;

            return (
              <div
                key={group}
                style={{
                  border: `1px solid ${color}33`,
                  borderRadius: 10, overflow: "hidden",
                  cursor: "pointer",
                  background: `linear-gradient(135deg, ${colorSoft}, transparent)`,
                  transition: "all 0.15s",
                }}
                onClick={() => setModalGroup({ group, color, colorSoft, items })}
              >
                <div style={{
                  ...s.groupHeader(color),
                  padding: "14px 16px", marginBottom: 0,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={s.groupTitle(color)}>{group}</span>
                    <span style={s.groupCount(hiddenInGroup, items.length)}>
                      {hiddenInGroup === 0 ? `${items.length} bật` : `${hiddenInGroup} tắt / ${items.length}`}
                    </span>
                  </div>
                  <span style={{ color: C.textDim, fontSize: 13 }}>›</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal chi tiết nhóm */}
        {modalGroup && (() => {
          const { group, color, colorSoft, items } = modalGroup;
          const allKeys = items.map(i => i.key);
          const hiddenInGroup = allKeys.filter(k => featHidden.includes(k)).length;
          return (
            <div
              style={{
                position: "fixed", inset: 0, zIndex: 1000,
                background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 24,
              }}
              onClick={() => setModalGroup(null)}
            >
              <div
                style={{
                  background: C.panel, border: `1px solid ${color}44`,
                  borderRadius: 16, width: "100%", maxWidth: 720,
                  maxHeight: "80vh", display: "flex", flexDirection: "column",
                  boxShadow: `0 0 60px ${color}22, 0 24px 64px rgba(0,0,0,0.6)`,
                }}
                onClick={e => e.stopPropagation()}
              >
                {/* Modal header */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "16px 20px", borderBottom: `1px solid ${color}33`,
                  background: `linear-gradient(135deg, ${colorSoft}, transparent)`,
                  borderRadius: "16px 16px 0 0",
                  flexShrink: 0,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ ...s.groupTitle(color), fontSize: 15 }}>{group}</span>
                    <span style={s.groupCount(hiddenInGroup, items.length)}>
                      {hiddenInGroup === 0 ? `${items.length} bật` : `${hiddenInGroup} tắt / ${items.length}`}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                      style={{ ...s.btn("ghost"), padding: "5px 12px", fontSize: 12, color }}
                      onClick={() => toggleGroup(group, allKeys, false)}
                    >
                      ✅ Bật nhóm
                    </button>
                    <button
                      style={{ ...s.btn("danger"), padding: "5px 12px", fontSize: 12 }}
                      onClick={() => toggleGroup(group, allKeys, true)}
                    >
                      🚫 Tắt nhóm
                    </button>
                    <button
                      style={{ background: "transparent", border: "none", color: C.textMuted, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 4px" }}
                      onClick={() => setModalGroup(null)}
                    >
                      ×
                    </button>
                  </div>
                </div>

                {/* Modal body */}
                <div style={{ overflowY: "auto", padding: "16px 20px" }}>
                  <div style={s.grid2}>
                    {items.map(({ key, label, desc }) => {
                      const visible = !featHidden.includes(key);
                      return (
                        <div
                          key={key}
                          style={s.featureRow(visible, color, colorSoft)}
                          onClick={() => toggleFeature(key)}
                        >
                          <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                            <div style={{
                              fontSize: 13, fontWeight: 600,
                              color: visible ? C.text : C.textDim,
                              marginBottom: 3,
                            }}>
                              {label}
                            </div>
                            <div style={{ fontSize: 11, color: visible ? C.textMuted : C.textDim, lineHeight: 1.4 }}>
                              {desc}
                            </div>
                            <div style={{
                              fontSize: 10, color: visible ? color : C.textDim,
                              marginTop: 4, fontFamily: "'Courier New', monospace",
                              opacity: 0.7,
                            }}>
                              {key}
                            </div>
                          </div>
                          <Toggle on={visible} onChange={() => toggleFeature(key)} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {filteredGroups.length === 0 && (
          <div style={{ textAlign: "center", padding: "32px 0", color: C.textMuted, fontSize: 13 }}>
            Không tìm thấy tính năng nào khớp với "{searchTerm}"
          </div>
        )}
      </div>

      {/* ── Other settings ── */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Cài đặt khác</div>
        <div
          style={{ ...s.toggleRow(config.hide_admin_settings), marginBottom: 12 }}
          onClick={() => setConfig(c => ({ ...c, hide_admin_settings: !c.hide_admin_settings }))}
        >
          <div>
            <div style={{ fontSize: 13, color: C.text }}>Ẩn trang Admin Settings với non-admin</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>Chỉ role admin mới thấy trang Settings</div>
          </div>
          <Toggle
            on={config.hide_admin_settings}
            onChange={() => setConfig(c => ({ ...c, hide_admin_settings: !c.hide_admin_settings }))}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={s.label}>Watermark thương hiệu (hiện ở footer admin)</label>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input
              style={{ ...s.input, flex: 1 }}
              value={config.creator_watermark || ""}
              onChange={e => setConfig(c => ({ ...c, creator_watermark: e.target.value }))}
              placeholder="Powered by YourBrand"
            />
            <Toggle
              on={config.creator_watermark_visible}
              onChange={() => setConfig(c => ({ ...c, creator_watermark_visible: !c.creator_watermark_visible }))}
            />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <div style={{ fontSize: 12, color: C.textMuted, display: "flex", alignItems: "center" }}>
          {totalHidden > 0
            ? <span style={{ color: C.amber }}>⚠ {totalHidden} tính năng đang bị tắt</span>
            : <span style={{ color: C.accent }}>✓ Tất cả tính năng đang bật</span>}
        </div>
        <button style={s.btn("primary")} onClick={onSave} disabled={saving}>
          {saving ? "Đang lưu..." : "💾 Lưu cấu hình"}
        </button>
      </div>
    </div>
  );
}

// ─── TAB: QUẢN LÝ USERS ─────────────────────────────────────
function TabUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", phone: "" });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/creator/users");
      setUsers(r.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ name: "", email: "", password: "", role: "staff", phone: "" });
    setEditUser(null);
    setShowForm(false);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email || "", password: u.password_plain || "", role: u.role, phone: u.phone || "" });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true); setMsg("");
    try {
      if (editUser) {
        const body = { name: form.name, role: form.role, phone: form.phone };
        if (form.password) body.password = form.password;
        if (form.email && (!editUser.email || editUser.pending_credentials)) body.email = form.email;
        await api.put(`/creator/users/${editUser.user_id}`, body);
        setMsg("✅ Đã cập nhật user" + (body.email && body.password ? " — Tài khoản đã được kích hoạt!" : ""));
      } else {
        await api.post("/creator/users", form);
        setMsg("✅ Đã thêm user mới");
      }
      await load();
      resetForm();
    } catch (e) {
      setMsg("✗ " + (e.response?.data?.detail || "Lỗi"));
    } finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/creator/users/${u.user_id}/toggle-active`);
      await load();
    } catch (e) {
      setMsg("✗ " + (e.response?.data?.detail || "Lỗi khi thay đổi trạng thái"));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Xóa user ${u.name}? Không thể hoàn tác!`)) return;
    try {
      await api.delete(`/creator/users/${u.user_id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Lỗi xóa");
    }
  };

  return (
    <div>
      {msg && (
        <div style={{
          padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.startsWith("✅") ? C.accentSoft2 : C.redSoft,
          color: msg.startsWith("✅") ? C.accent : C.red,
          border: `1px solid ${msg.startsWith("✅") ? C.borderGlow : C.red + "44"}`,
        }}>
          {msg}
        </div>
      )}

      {showForm && (
        <div style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
              {editUser ? `✏️ Chỉnh sửa: ${editUser.name}` : "➕ Thêm User mới"}
            </div>
            <button style={s.btn("muted")} onClick={resetForm}>✕ Đóng</button>
          </div>
          <div style={s.grid2}>
            <Input label="Họ tên *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nguyễn Văn A" />
            {editUser && editUser.pending_credentials ? (
              <div>
                <Input
                  label="🔑 Gán Email đăng nhập *"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="nhanvien@email.com"
                />
                <div style={{ fontSize: 10, color: C.amber, marginTop: -8, marginBottom: 8 }}>
                  Nhân viên này chưa có tài khoản — nhập email + mật khẩu để kích hoạt
                </div>
              </div>
            ) : (
              <Input
                label={editUser ? "Email (chỉ đọc)" : "Email *"}
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="nhanvien@email.com"
                disabled={!!editUser}
              />
            )}
            <div>
              <label style={s.label}>{editUser ? "Mật khẩu (hiện tại / đổi mới)" : "Mật khẩu *"}</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={editUser ? "Nhập để đổi mật khẩu" : "Ít nhất 6 ký tự"}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "8px 36px 8px 10px", borderRadius: 7, fontSize: 13,
                    border: `1px solid ${C.border}`, background: C.card,
                    color: C.text, outline: "none",
                    WebkitTextFillColor: C.text,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    color: C.textDim, fontSize: 15, padding: 2, lineHeight: 1,
                  }}
                  title={showPw ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                >
                  {showPw ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
            <Input label="Số điện thoại" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="0901234567" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>Role *</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {ROLES.map(r => (
                <button key={r} style={{
                  ...s.btn(form.role === r ? "primary" : "muted"),
                  padding: "6px 14px", fontSize: 12,
                }} onClick={() => setForm(f => ({ ...f, role: r }))}>
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button style={s.btn("muted")} onClick={resetForm}>Hủy</button>
            <button style={s.btn("primary")} onClick={handleSave} disabled={saving}>
              {saving ? "Đang lưu..." : editUser ? "💾 Cập nhật" : "➕ Tạo User"}
            </button>
          </div>
        </div>
      )}

      <div style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={s.sectionTitle}>Danh sách Users ({users.length})</div>
          <button style={s.btn("ghost")} onClick={() => { resetForm(); setShowForm(true); }}>
            ➕ Thêm User mới
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: C.textMuted }}>Đang tải...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Tên", "Email", "Role", "SĐT", "Trạng thái", "Thao tác"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: C.textMuted, fontWeight: 600, fontSize: 11, letterSpacing: "0.08em" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} style={{ borderBottom: `1px solid ${C.border}22` }}>
                    <td style={{ padding: "10px 12px", color: C.text, fontWeight: 600 }}>{u.name}</td>
                    <td style={{ padding: "10px 12px", color: C.textMuted }}>{u.email}</td>
                    <td style={{ padding: "10px 12px" }}><span style={s.badge(u.role)}>{ROLE_LABELS[u.role]}</span></td>
                    <td style={{ padding: "10px 12px", color: C.textMuted }}>{u.phone || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {u.pending_credentials ? (
                        <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 20, fontWeight: 700, background: C.amberSoft, color: C.amber, border: `1px solid ${C.amber}44` }}>
                          ⏳ Chờ cấp TK
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 20, fontWeight: 700, background: u.is_active ? C.accentSoft2 : C.redSoft, color: u.is_active ? C.accent : C.red }}>
                          {u.is_active ? "Hoạt động" : "Đã khóa"}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={{ ...s.btn("ghost"), padding: "4px 10px", fontSize: 11 }} onClick={() => openEdit(u)}>✏️</button>
                        <button
                          style={{ ...s.btn(u.is_active ? "danger" : "ghost"), padding: "4px 10px", fontSize: 11 }}
                          onClick={() => toggleActive(u)}
                        >
                          {u.is_active ? "🔒" : "🔓"}
                        </button>
                        <button style={{ ...s.btn("danger"), padding: "4px 10px", fontSize: 11 }} onClick={() => deleteUser(u)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TAB: IP WHITELIST ────────────────────────────────────────
function TabIP({ config, setConfig, onSave, saving }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState("");

  const list = config.ip_whitelist || [];

  const validateIP = (ip) => {
    // IPv4 hoặc IPv4 CIDR
    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    // IPv6 đơn giản
    const ipv6 = /^[0-9a-fA-F:]+$/;
    return ipv4.test(ip) || ipv6.test(ip);
  };

  const addIP = () => {
    const val = input.trim();
    if (!val) return;
    if (!validateIP(val)) { setError("IP không hợp lệ. Ví dụ: 192.168.1.1 hoặc 14.161.22.100"); return; }
    if (list.includes(val)) { setError("IP này đã có trong danh sách"); return; }
    setError("");
    setConfig(c => ({ ...c, ip_whitelist: [...(c.ip_whitelist || []), val] }));
    setInput("");
  };

  const removeIP = (ip) => {
    setConfig(c => ({ ...c, ip_whitelist: (c.ip_whitelist || []).filter(x => x !== ip) }));
  };

  return (
    <div>
      {/* Warning banner */}
      <div style={{
        background: "#ff9f0a15", border: "1px solid #ff9f0a33",
        borderRadius: 12, padding: "14px 18px", marginBottom: 20,
        display: "flex", gap: 12, alignItems: "flex-start",
      }}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#ff9f0a", marginBottom: 4 }}>
            Cẩn thận khi thêm IP
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>
            Nếu danh sách <b style={{ color: C.text }}>trống</b> → mọi người đều vào được (mặc định).
            Nếu có ít nhất 1 IP → <b style={{ color: C.text }}>chỉ IP đó mới vào được</b>, tất cả IP khác bị chặn.
            Hãy chắc chắn IP của bạn đã có trong danh sách trước khi lưu.
          </div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.sectionTitle}>
          🛡️ Danh sách IP được phép
          <span style={{
            marginLeft: 10, fontSize: 11, fontWeight: 500,
            background: list.length > 0 ? C.accentSoft2 : C.redSoft,
            color: list.length > 0 ? C.accent : C.red,
            padding: "2px 10px", borderRadius: 20,
          }}>
            {list.length === 0 ? "Mở cho tất cả" : `${list.length} IP`}
          </span>
        </div>

        {/* Input thêm IP */}
        <div style={{ display: "flex", gap: 10, marginBottom: 6 }}>
          <input
            style={{ ...s.input, flex: 1, fontFamily: "'Courier New', monospace", fontSize: 13 }}
            value={input}
            onChange={e => { setInput(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && addIP()}
            placeholder="Nhập địa chỉ IP... (vd: 14.161.22.100)"
          />
          <button style={{ ...s.btn("primary"), padding: "8px 18px", fontSize: 13 }} onClick={addIP}>
            + Thêm IP
          </button>
        </div>
        {error && (
          <div style={{ fontSize: 12, color: C.red, marginBottom: 10 }}>⚠ {error}</div>
        )}

        {/* Danh sách IP */}
        <div style={{ marginTop: 16 }}>
          {list.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "32px 0",
              color: C.textDim, fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 10,
            }}>
              Chưa có IP nào — tất cả đều có thể truy cập
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {list.map((ip, idx) => (
                <div key={ip} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: 8, padding: "10px 14px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{
                      fontSize: 11, color: C.textDim, fontWeight: 600,
                      background: C.panel, borderRadius: 4, padding: "1px 7px",
                    }}>#{idx + 1}</span>
                    <span style={{
                      fontFamily: "'Courier New', monospace", fontSize: 14,
                      color: C.accent, letterSpacing: "0.04em",
                    }}>{ip}</span>
                  </div>
                  <button
                    style={{
                      background: C.redSoft, border: `1px solid ${C.red}33`,
                      color: C.red, borderRadius: 6, padding: "4px 12px",
                      fontSize: 12, cursor: "pointer", fontWeight: 600,
                    }}
                    onClick={() => removeIP(ip)}
                  >
                    Xoá
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Nút lưu */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button
            style={{ ...s.btn("primary"), padding: "10px 28px" }}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Đang lưu..." : "💾 Lưu IP Whitelist"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TAB: HƯỚNG DẪN SỬ DỤNG FEATURE TOGGLES ────────────────
function TabGuide() {
  const codeUseFeature = `// 1. Import hook vào bất kỳ trang nào
import { useFeature } from "../contexts/CreatorConfigContext";

// 2. Dùng trong component
export default function Products() {
  const canSeeRecipe    = useFeature("products.recipe");
  const canDeleteProduct = useFeature("products.delete");
  const canImport       = useFeature("products.import");

  return (
    <>
      {/* Nút chỉ hiện khi Creator bật */}
      {canSeeRecipe && (
        <Button onClick={openRecipe}>📖 Công thức NVL</Button>
      )}
      {canDeleteProduct && (
        <Button variant="danger" onClick={handleDelete}>Xóa</Button>
      )}
    </>
  );
}`;

  const codeAllKeys = FEATURE_GROUPS.flatMap(g =>
    g.items.map(i => `  "${i.key}"   // ${i.label}`)
  ).join("\n");

  const CodeBlock = ({ code }) => (
    <pre style={{
      background: "#060d08", border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "14px 16px", fontSize: 12, color: "#7dd3a8", overflowX: "auto",
      lineHeight: 1.7, marginTop: 8,
    }}>{code}</pre>
  );

  return (
    <div>
      <div style={s.card}>
        <div style={s.sectionTitle}>Cách dùng useFeature() trong các trang</div>
        <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 8 }}>
          Import hook <code style={{ color: C.accent }}>useFeature</code> rồi bọc bất kỳ UI nào cần ẩn:
        </div>
        <CodeBlock code={codeUseFeature} />
      </div>

      <div style={s.card}>
        <div style={s.sectionTitle}>Toàn bộ Feature Keys có sẵn</div>
        <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 8 }}>
          Dùng đúng key bên dưới trong <code style={{ color: C.accent }}>useFeature("key")</code>:
        </div>
        <CodeBlock code={`// Tất cả ${ALL_FEATURE_KEYS.length} feature keys:\n[\n${codeAllKeys}\n]`} />
      </div>

      <div style={s.card}>
        <div style={s.sectionTitle}>Lưu ý bảo mật</div>
        <div style={{ fontSize: 13, color: C.textMuted, lineHeight: 2.2 }}>
          <div>🔐 Feature toggle chỉ ẩn UI — không chặn API. Phân quyền thật vẫn nằm ở backend.</div>
          <div>🚫 Đặt <code style={{ color: C.accent }}>CREATOR_PASSWORD</code> mạnh trong <code style={{ color: C.accent }}>.env</code> — ít nhất 20 ký tự ngẫu nhiên</div>
          <div>🍪 Session Creator dùng httpOnly cookie — expire sau 24 giờ</div>
          <div>💾 Config lưu MongoDB → đồng bộ mọi browser ngay lập tức sau Lưu</div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────
export default function CreatorPanel() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState("stats");
  const [config, setConfig] = useState({
    hidden_nav: [], hidden_features: [],
    hide_admin_settings: false,
    creator_watermark: "Powered by CreatorDev",
    creator_watermark_visible: true,
    ip_whitelist: [],
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    api.get("/creator/verify")
      .then(() => {
        setAuthed(true);
        return api.get("/creator/config");
      })
      .then(r => { if (r) setConfig(r.data); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = async () => {
    setAuthed(true);
    try {
      const r = await api.get("/creator/config");
      setConfig(r.data);
    } catch {}
  };

  const handleLogout = async () => {
    await api.post("/creator/logout").catch(() => {});
    setAuthed(false);
  };

  const handleSave = async () => {
    setSaving(true); setSaveMsg("");
    try {
      await api.put("/creator/config", config);
      setSaveMsg("✅ Đã lưu cấu hình vào MongoDB");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch {
      setSaveMsg("✗ Lưu thất bại");
    } finally { setSaving(false); }
  };

  if (checking) {
    return (
      <div style={{ ...s.loginWrap, fontSize: 13, color: C.textMuted }}>
        Đang kiểm tra phiên đăng nhập...
      </div>
    );
  }

  if (!authed) return <LoginScreen onLogin={handleLogin} />;

  const hiddenFeatCount = (config.hidden_features || []).length;
  const hiddenNavCount  = (config.hidden_nav || []).length;

  const TABS = [
    { key: "stats",    label: "📊 Thống kê" },
    { key: "features", label: `⚙️ Ẩn/Hiện UI${hiddenFeatCount + hiddenNavCount > 0 ? ` (${hiddenFeatCount + hiddenNavCount})` : ""}` },
    { key: "ip",       label: `🛡️ IP Whitelist${(config.ip_whitelist || []).length > 0 ? ` (${config.ip_whitelist.length})` : ""}` },
    { key: "users",    label: "👤 Quản lý Users" },
    { key: "guide",    label: "📖 Hướng dẫn" },
    { key: "history",  label: "🕵️ Lịch sử" },
  ];

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `linear-gradient(135deg, ${C.accent}, ${C.accentDim})`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
          }}>⚡</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: "0.06em", color: C.text }}>
              CREATOR PANEL
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, letterSpacing: "0.1em" }}>
              Full Control · Restricted Access
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {saveMsg && (
            <div style={{
              fontSize: 12, padding: "4px 12px", borderRadius: 20,
              background: saveMsg.startsWith("✅") ? C.accentSoft2 : C.redSoft,
              color: saveMsg.startsWith("✅") ? C.accent : C.red,
            }}>
              {saveMsg}
            </div>
          )}
          <button
            style={{ ...s.btn("danger"), padding: "6px 14px", fontSize: 12 }}
            onClick={handleLogout}
          >
            Đăng xuất
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={s.main}>
        <div style={s.tabs}>
          {TABS.map(t => (
            <button key={t.key} style={s.tab(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "stats"    && <TabStats />}
        {activeTab === "features" && (
          <TabFeatures config={config} setConfig={setConfig} onSave={handleSave} saving={saving} />
        )}
        {activeTab === "ip"       && (
          <TabIP config={config} setConfig={setConfig} onSave={handleSave} saving={saving} />
        )}
        {activeTab === "users"   && <TabUsers />}
        {activeTab === "guide"   && <TabGuide />}
        {activeTab === "history"  && <TabHistory />}
      </div>
    </div>
  );
}